import { get, isObject, mapValues } from 'lodash'

type HelperFunc<TTheme, TValue> = {
	(props: { theme: TTheme }): TValue
	with(...funcs: Array<(value: TValue) => TValue>): (props: { theme: TTheme }) => TValue
}

type Helper<TTheme, TSubtheme> = {
	[K in keyof TSubtheme]: TSubtheme[K] extends object ? Helper<TTheme, TSubtheme[K]> : HelperFunc<TTheme, TSubtheme[K]>
}

const recursiveMapValues = <TTheme, TSubtheme>(
	value: TSubtheme,
	path: Array<string>,
): Helper<TTheme, TSubtheme> | HelperFunc<TTheme, TSubtheme> => {
	if (isObject(value)) {
		return mapValues(value as {}, (prop, key) => recursiveMapValues(prop, [...path, key])) as Helper<TTheme, TSubtheme>
	}
	// Cannot build functions with extra props, so have to treat them as any
	const getter: HelperFunc<TTheme, TSubtheme> = ({ theme }) => get(theme, path)

	getter.with = (...funcs) => {
		if (funcs.length === 1) {
			// Skip reduce in the most common case of a single function
			return (props) => funcs[0](getter(props))
		}
		return (props) => funcs.reduce((current, func) => func(current), getter(props))
	}

	return getter
}

/**
 * Create a matching structure with a getter-function for each non-object property.
 *
 * Example:
 * ```ts
 * const theme = { nested: { color: '#FFF' } }
 * const helpers = themeHelpers(theme)
 * styled.div`
 * 	// Equivalent
 * 	color: ${helpers.nested.color};
 * 	color: ${({ theme }) => theme.nested.color};
 *
 * 	// Equivalent
 * 	color: ${helpers.nested.color.with(lighten(0.2))};
 * 	color: ${({ theme }) => lighten(0.2, theme.nested.color)};
 * `
 * ```
 */
export const themeHelpers = <TTheme>(theme: TTheme) =>
	recursiveMapValues<TTheme, TTheme>(theme, []) as Helper<TTheme, TTheme>
